import { create } from 'zustand';
import type { AuctionState, AuctionCategory } from '@auction/shared';
import { api } from '../lib/api';

// List API returns simplified format
export interface AuctionListItem {
  id: string;
  title: string;
  imageUrl: string | null;
  category: AuctionCategory;
  currentPrice: number;
  bidCount: number;
  status: string;
  endTime: string;
  sellerName: string;
}

interface AuctionStore {
  auctions: AuctionListItem[];
  loading: boolean;
  error: string | null;
  selectedCategory: AuctionCategory | null;
  setSelectedCategory: (category: AuctionCategory | null) => void;
  fetchAuctions: (status?: string, category?: AuctionCategory | null) => Promise<void>;
  fetchAuction: (id: string) => Promise<AuctionState | null>;
  createAuction: (data: Partial<AuctionState>) => Promise<AuctionState>;
  placeBid: (auctionId: string, amount: number) => Promise<void>;
  cancelAuction: (id: string) => Promise<void>;
  updateAuctionLocally: (auction: AuctionState) => void;
}

export const useAuctionStore = create<AuctionStore>((set, get) => ({
  auctions: [],
  loading: false,
  error: null,
  selectedCategory: null,

  setSelectedCategory: (category) => {
    set({ selectedCategory: category });
    get().fetchAuctions(undefined, category);
  },

  fetchAuctions: async (status?: string, category?: AuctionCategory | null) => {
    set({ loading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (category) params.set('category', category);
      const queryString = params.toString();
      const url = `/api/auctions${queryString ? `?${queryString}` : ''}`;
      const response = await api.get<{ data: { items: AuctionListItem[] } }>(url);
      set({ auctions: response.data?.items || [], loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false, auctions: [] });
    }
  },

  fetchAuction: async (id: string) => {
    try {
      const response = await api.get<{ data: AuctionState }>(`/api/auctions/${id}`);
      return response.data || null;
    } catch {
      return null;
    }
  },

  createAuction: async (data: Partial<AuctionState>) => {
    const response = await api.post<{ data: { id: string } }>('/api/auctions', data);
    // Fetch the full auction after creation
    const created = await api.get<{ data: AuctionState }>(`/api/auctions/${response.data.id}`);
    if (created.data) {
      // Refetch list to get the new item in correct format
      const listResponse = await api.get<{ data: { items: AuctionListItem[] } }>('/api/auctions');
      set({ auctions: listResponse.data?.items || [] });
      return created.data;
    }
    return response.data as unknown as AuctionState;
  },

  placeBid: async (auctionId: string, amount: number) => {
    await api.post(`/api/auctions/${auctionId}/bids`, { amount });
  },

  cancelAuction: async (id: string) => {
    await api.delete(`/api/auctions/${id}`);
    set((state) => ({
      auctions: state.auctions.filter((a) => a.id !== id),
    }));
  },

  updateAuctionLocally: (_auction: AuctionState) => {
    // Note: List format is different from full AuctionState
    // Refetching would be needed for accurate updates
    // For now, this is a no-op as WebSocket updates come through differently
  },
}));
